import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IField, IProtocol, IVersione } from '../../../../../shared/interfaces';
import { RootState } from '../../../../../store';
import versioniService from '../../../../../services/versioni.service';
import CircleLoader from '../../../../Loader/CircleLoader/CircleLoader';
import { ActivityItem, DetailsRow, FontWeights, GroupedList, GroupHeader, IActivityItemProps, IColumn, Icon, IDetailsColumnStyleProps, IDetailsColumnStyles, IDetailsRowStyles, IGroup, IGroupHeaderProps, IGroupRenderProps, Link, mergeStyleSets, SelectionMode } from '@fluentui/react';
import Utilities from '../../../../../shared/Utilities/utilities';
import { myTheme } from '../../../../../assets/styles/my-theme';
import AppUtilities from '../../../../../shared/Utilities/appUtilities';

const { palette } = myTheme;

let classNames = mergeStyleSets({
    padding: {
        paddingBottom: '3px',
        paddingTop: '3px'
    },
    headerMargins: {
        marginTop: '20px',
        marginBottom: '20px'
    },
    nameText: {
        fontWeight: 'bold',
    },
    columnWidth: {
        width: '200px'
    }
});

export interface VersioniProps {
    protocol: IProtocol
}

const groupProps: IGroupRenderProps = {
    isAllGroupsCollapsed: true,
    onRenderHeader: (props?: IGroupHeaderProps): JSX.Element => (
        <GroupHeader onRenderTitle={onRenderTitle} {...props} className={`${classNames.padding} ${classNames.headerMargins}`} />
    )

}

const onRenderTitle = (props?: IGroupHeaderProps): JSX.Element => {

    const myHeader = {
        name: props?.group?.name,
        numero: props?.group?.data.numeroVersione,
        utenteModifica: props?.group?.data.utenteModifica,
        data: Utilities.FormatDate(props?.group?.data.dataVersione, false)
    }
    const item: IActivityItemProps = {
        activityDescription: [
            <div>
                <Link key={2} className={classNames.nameText}>{myHeader.name}</Link>
                <div key={1}>{myHeader.utenteModifica}</div>
            </div>
        ],
        timeStamp: [
            <div key={1}>{myHeader.data}</div>
        ]
    }
    return (
        <ActivityItem {...item} />
    )
}


const Versioni: React.FunctionComponent<VersioniProps> = (props) => {

    const selectCurrentAoo = (state: RootState) => state.sessionReducer.currentAoo;
    const currentAoo = useSelector(selectCurrentAoo);
    const selectCurrentUor = (state: RootState) => state.sessionReducer.currentUor;
    const currentUor = useSelector(selectCurrentUor);

    const [loading, setLoading] = useState(false);
    const [versioniGroupedList, setVersioniGroupedList] = useState<{ items: any[], groups: IGroup[] }[]>([]);

    const columns: IColumn[] = [
        {
            key: 'modified',
            name: 'modified',
            iconName: 'Page',
            isIconOnly: true,
            fieldName: 'modified',
            minWidth: 16,
            maxWidth: 16,
            onRender: () => {
                return <Icon iconName="pageEdit" />
            }
        },
        {
            key: "nomeCampo",
            name: "nomeCampo",
            fieldName: "nomeCampo",
            minWidth: 300,
            className: classNames.nameText,
            isResizable: true
        },
        {
            key: "valoreCampo",
            name: "valoreCampo",
            fieldName: "valoreCampo",
            minWidth: 300,
            isMultiline: true,

        }
    ]


    useEffect(() => {
        if (currentAoo && props.protocol) {
            getVersioni();
        }
    }, [props.protocol, currentAoo]);


    const getVersioni = () => {
        setLoading(true);

        versioniService.getVersioni(currentAoo!.id, props.protocol!.id!, currentUor?.id)
            .then(response => {
                let versioniList = remapVersioni(response);
                setVersioniGroupedList(versioniList);
                setLoading(false);
            })
            .catch(error => {
                console.error("Errore nel recupero delle versioni", error);
                setLoading(false);
            });

    }


    const remapVersioni = (response: IVersione[]) => {

        let mapping = response.map(
            (versione) => {
                return {
                    items: remapCampi(versione),
                    groups: [
                        {
                            key: versione.numeroVersione.toString(),
                            name: 'Versione ' + versione.numeroVersione.toString(),
                            startIndex: 0,
                            count: versione.campi.length,
                            data: versione,
                            level: 0
                        } as IGroup]
                }
            }
        );

        return mapping;
    }


    const remapCampi = (versione: IVersione) => {
        let mapping = versione.campi.map(
            (campo) => {
                let obj = {
                    nomeCampo: campo.displayName,
                    valoreCampo: AppUtilities.getDisplayValueFromFieldControlType(campo),
                    isModified: false
                }
                if (versione.campiModificati.includes(campo.internalName)) {

                    obj.isModified = true;

                }
                return obj;
            }
        );

        return mapping;
    }





    const onRenderCell = (nestingDepth?: number, item?: any, itemIndex?: number, group?: IGroup): React.ReactNode => {

        nestingDepth = 0;
        let visibilityStr = item.isModified ? '' : 'hidden';
        let detailsRowStyles: Partial<IDetailsRowStyles> = {
            root: {
                display: 'flex',
                width: '100%',
                ...(item.isModified ? { backgroundColor: palette.themeLight } : {}),
                flex: 1,
                '& .ms-DetailsRow-fields': {
                    width: '100%'
                },
                '& .ms-DetailsRow-cell:first-child': {
                    flex: '0 0 auto',
                    display: 'flex',
                    visibility: visibilityStr,
                    paddingRight: 0,
                    alignSelf: 'center',
                    alignItems: 'center'
                },
                '& .ms-DetailsRow-cell:nth-child(2)': {
                    flex: '0 1 200px'
                },
                '& .ms-DetailsRow-cell:nth-child(3)': {
                    flex: '1 1 200px'
                }
            }
        }

        return (

            <DetailsRow
                columns={columns}
                groupNestingDepth={nestingDepth}
                item={item}
                itemIndex={itemIndex!}
                selectionMode={SelectionMode.none}
                group={group}
                styles={detailsRowStyles}
            />
        );
    }


    return (

        <div className={`flex flex-column position-relative`}>
            <CircleLoader loaderClass='loader-large' loading={loading} />
            <div className={'my-scroll-div position-relative'}>
                <div className={`my-component-content`}>
                    {versioniGroupedList.map(
                        (versione, index) => (

                            <GroupedList
                                key={index}
                                items={versione.items}
                                onRenderCell={onRenderCell}
                                selectionMode={SelectionMode.none}
                                groups={versione.groups}
                                groupProps={groupProps}
                            />
                        )
                    )}
                </div>
            </div>
        </div>

    );

}

export default Versioni;